import {
	HttpClient,
	HttpEvent,
	HttpEventType,
	HttpHeaders
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import {
	catchError,
	EMPTY,
	expand,
	filter,
	map,
	Observable,
	throwError
} from 'rxjs';
import {
	ChunkedFileUploadRequest,
	ChunkedFileUploadResponse
} from '@twrx-api-models';
import {
	bytesInMB,
	completedFileProgress,
	fileUploadChunkSizeInMB
} from 'app/common/app-constants';
import { combineCompleteUrl } from 'app/common/utils/utils';
import { isNullOrUndefined } from 'app/common/utils/utils.object';
import { FormFileModel } from 'app/models/dto/form-file-model';
import { UploadFileResponse } from 'app/models/dto/upload-file-response';

import { BaseHttpApiService } from './base-http-api.service';

@Injectable()
export class HttpFileApiService extends BaseHttpApiService {
	constructor(private readonly httpClient: HttpClient) {
		super();
	}

	public downloadFile(
		url: string,
		data?: unknown,
		cancellationSubject?: Observable<void>
	): Observable<number> {
		return this.httpClient
			.post(combineCompleteUrl(url), data, {
				responseType:   'arraybuffer',
				reportProgress: true,
				observe:        'events'
			})
			.pipe(
				map((x: HttpEvent<ArrayBuffer>) =>
					this.getDownloadFileProgress(x)
				),
				filter(x => x !== null),
				this.applyCancellationSubject(cancellationSubject)
			);
	}

	public getFile(
		url: string,
		cancellationSubject?: Observable<void>,
		noncritical?: boolean
	): Observable<Blob> {
		return this.httpClient
			.get(combineCompleteUrl(url), {
				...this.getRequestOptions(noncritical),
				responseType: 'arraybuffer',
				observe:      'response'
			})
			.pipe(
				map(
					response =>
						new Blob([response.body], {
							type: response.headers.get('Content-Type') ?? ''
						})
				),
				this.applyCancellationSubject(cancellationSubject)
			);
	}

	public postForm<T>(
		url: string,
		data: object,
		files: FormFileModel[],
		cancellationSubject?: Observable<void>
	): Observable<T> {
		return this.httpClient
			.post<T>(combineCompleteUrl(url), this.toFormData(data, files))
			.pipe(this.applyCancellationSubject(cancellationSubject));
	}

	public putForm<T>(
		url: string,
		data: object,
		files: FormFileModel[],
		cancellationSubject?: Observable<void>
	): Observable<T> {
		return this.httpClient
			.put<T>(combineCompleteUrl(url), this.toFormData(data, files))
			.pipe(this.applyCancellationSubject(cancellationSubject));
	}

	public uploadFile<T>(
		url: string,
		file: File,
		uploadRequest?: ChunkedFileUploadRequest,
		cancellationSubject?: Observable<void>
	): Observable<UploadFileResponse<T>> {
		const chunkSize = fileUploadChunkSizeInMB * bytesInMB;
		const fileSize = file.size;
		const chunksCount = Math.ceil(fileSize / chunkSize);

		uploadRequest = {
			...uploadRequest,
			chunkIndex: 0,
			fileSize,
			chunksCount
		};

		const getProgress = (chunkIndex: number): number =>
			((chunkIndex + 1) / chunksCount) * completedFileProgress;

		return this.uploadFileChunk<T>(
			file,
			url,
			chunkSize,
			uploadRequest
		).pipe(
			expand(
				(
					{
						chunkIds,
						fileName,
						fileGuid,
						fileSystemName,
						additionalPath
					},
					chunkIndex
				) => {
					if (chunkIndex + 1 >= chunksCount) {
						return EMPTY;
					}

					return this.uploadFileChunk<T>(file, url, chunkSize, {
						...uploadRequest,
						fileName,
						fileGuid,
						fileSystemName,
						additionalPath,
						chunkIndex: chunkIndex + 1,
						chunkIds
					}).pipe(
						map(uploadResponse => ({
							...uploadResponse,
							progress: getProgress(chunkIndex + 1)
						})),
						catchError(x => throwError(() => x))
					);
				}
			),
			map(({ data, chunkIds, progress }) => ({
				data,
				chunkIds,
				progress
			})),
			this.applyCancellationSubject(cancellationSubject)
		);
	}

	private getDownloadFileProgress(event: HttpEvent<ArrayBuffer>): number {
		if (event.type === HttpEventType.DownloadProgress) {
			return Math.round(
				(completedFileProgress * event.loaded) / event.total
			);
		}

		if (event.type === HttpEventType.Response) {
			const fileName = this.getFileName(event.headers);
			const contentType = event.headers.get('content-type');
			const blob = new Blob([event.body], {
				type: contentType
			});

			saveAs(blob, fileName);
		}

		return null;
	}

	private getFileName(headers: HttpHeaders): string {
		let fileName = headers
			.get('Content-Disposition')
			.split(';')[1]
			.trim()
			.split('=')[1]
			.split('"')[1];

		if (!fileName) {
			fileName = headers
				.get('Content-Disposition')
				.split(';')[1]
				.trim()
				.split('=')[1];
		}

		return fileName;
	}

	private uploadFileChunk<T>(
		file: File,
		url: string,
		chunkSize: number,
		data: ChunkedFileUploadRequest
	): Observable<ChunkedFileUploadResponse<T>> {
		const totalFileSize = file.size;

		const { chunkIndex } = data;
		const start = chunkIndex * chunkSize;
		const end = Math.min(start + chunkSize, totalFileSize);

		const filePart = file.slice(start, end);

		return this.httpClient.post<ChunkedFileUploadResponse<T>>(
			combineCompleteUrl(url),
			this.toFormData(data, [
				{ field: 'file', file: filePart, fileName: file.name }
			])
		);
	}

	private toFormData(data: object, files: FormFileModel[]): FormData {
		const formData = new FormData();

		files.forEach(({ field, file, fileName }) =>
			formData.append(field, file, fileName)
		);
		Object.entries(data)
			.filter(([, value]) => !isNullOrUndefined(value))
			.forEach(([key, value]) => {
				if (Array.isArray(value)) {
					value.forEach(item => formData.append(key, String(item)));
				} else {
					formData.append(key, String(value));
				}
			});

		return formData;
	}
}
